import moment from 'moment';

import helper from 'actions/helper';
import string from 'actions/helper/string';

const msInDay = 24 * 60 * 60 * 1000;
const msInMinute = 1 * 60 * 1000;

const obj = {
    msInDay,
    msInMinute,
    tz: 0,

    get weekNamesShort() {
        const arr = [];
        for (let i = 1; i < 8; i++) {
            arr.push(this.getWeekNameShort(i));
        }

        return arr;
    },

    getWeekNameShort(flag) {
        return moment.weekdaysShort(flag);
    },

    getWeekNumber(date) {
        return moment(date).isoWeek();
    },

    getWeek(date) {
        const weekNumber = moment(date).isoWeek();
        const isoYear = moment(date).isoWeekYear();
        const startOfWeek = moment().isoWeekYear(isoYear).isoWeek(weekNumber).startOf('isoWeek').toDate();
        const endOfWeek = moment().isoWeekYear(isoYear).isoWeek(weekNumber).endOf('isoWeek').toDate();

        return { startOfWeek, endOfWeek };
    },

    getWeekString(date) {
        const { startOfWeek, endOfWeek } = this.getWeek(date);
        return `${moment(startOfWeek).format('DD MMMM')} - ${moment(endOfWeek).format('DD MMMM YYYY')}`;
    },

    getMonthName(date) {
        return moment(date).format('MMMM YYYY');
    },

    toStr(date, format = 'YYYY-MM-DDTHH:mm:ss.000+00:00', useTz) {
        return moment(this.create(date, useTz)).format(format);
    },

    toTodayStr(date, format = "D MMMM") {
        let [first, last] = this.toStr(date, `${format},dddd`).split(",");
        last = string.capitalize(last);

        return `${first}, ${last}`;
    },

    toDateStr(date) {
        return this.toStr(date, "DD.MM.YYYY");
    },

    toDateServerStr(date) {
        return this.toStr(date, "YYYY-MM-DD");
    },

    toTimeStr(date, isShort = true) {
        return this.toStr(date, isShort ? "HH:mm" : "HH:mm:ss");
    },

    toTimeDateStr(date) {
        return this.toStr(date, "HH:mm DD.MM.YYYY");
    },

    toDateTimeStr(date, useTz) {
        return this.toStr(date, "DD.MM.YYYY HH:mm", useTz);
    },

    toTimestamp(date) {
        return this.toStr(date, "YYYY-MM-DD HH:mm:ss");
    },

    diffFrom(d, d2, isCurrent = true) {
        return moment(this.create(d)).from(moment(this.create(d2)), isCurrent);
    },

    formatTimeDuration(date, date2) {
        const time = date.getTime();
        const time2 = date2.getTime();

        const diffTime = Math.abs(time - time2);
        const diffMins = diffTime / this.msInMinute;
        const hours = Math.floor(diffMins / 60);
        const mins = diffMins - (hours * 60);

        const l = hours > 0 ? `${hours}${helper.tr("main.hAbbr")} ` : "";

        const d = new Date();
        const d2 = new Date();

        d.setHours(0, 0, 0, 0);
        d2.setHours(0, mins, 0, 0);

        if (mins < 5)
            return l;

        return l + `${mins} ${helper.tr("main.min")}`;
    },

    getMonthDaysCount(date) {
        const d = new Date(date.getTime());
        d.setDate(1);
        d.setMonth(d.getMonth() + 1);
        d.setDate(d.getDate() - 1);

        return d.getDate();
    },

    addMonth(date, days) {
        const d = this.create(date);
        const currDay = d.getDate();

        const newDate = this.create(date);
        newDate.setDate(1);
        newDate.setMonth(newDate.getMonth() + days);

        const lastDay = this.getMonthDaysCount(newDate);
        const day = currDay > lastDay ? lastDay : currDay;

        newDate.setDate(day);

        return newDate;
    },

    addDays(date, days) {
        const newDate = this.create(date);
        newDate.setDate(newDate.getDate() + days);

        return newDate;
    },

    addHours(date, hours) {
        const newDate = this.create(date);
        newDate.setHours(newDate.getHours() + hours);

        return newDate;
    },
    addMins(date, mins) {
        const newDate = this.create(date);
        newDate.setMinutes(newDate.getMinutes() + mins);

        return newDate;
    },

    equealDates(d, d2) {
        return d.getFullYear() === d2.getFullYear() && d.getMonth() === d2.getMonth() && d.getDate() === d2.getDate();
    },

    equealTiems(d, d2) {
        return d.getHours() === d2.getHours() && d.getMinutes() === d2.getMinutes() && d.getSeconds() === d2.getSeconds();
    },

    equealDataTimes(d1, d2) {
        return d1.getFullYear() === d2.getFullYear() &&
            d1.getMonth() === d2.getMonth() &&
            d1.getDate() === d2.getDate() &&
            d1.getHours() === d2.getHours() &&
            d1.getMinutes() === d2.getMinutes();
    },

    diffTime(d, d2) {
        const date2 = new Date(d2.getTime());
        date2.setFullYear(d.getFullYear());
        date2.setMonth(d.getMonth());
        date2.setDate(d.getDate());

        return d.getTime() - date2.getTime();
    },

    diffTime2(d, d2) {
        return d.getTime() - d2.getTime();
    },

    daysDiff(d, d2) {
        const utcDate1 = new Date(d.toUTCString());
        const utcDate2 = new Date(d2.toUTCString());
        const timeDiff = Math.abs(utcDate2 - utcDate1);
        const daysDiff = Math.ceil(timeDiff / (1000 * 60 * 60 * 24));
        return daysDiff;
    },

    clearTime(date) {
        const d = this.create(date);
        d.setHours(0, 0, 0, 0);

        return d;
    },

    getAgeStr(date) {
        const number = this.getAge(date);

        const currentLocale = moment.locale();

        if (currentLocale === "en") {
            return "age " + number;
        }

        let remainder10 = number % 10;
        let remainder100 = number % 100;

        if (remainder10 === 1 && remainder100 !== 11) {
            return number + " год";
        }

        if ([2, 3, 4].includes(remainder10) && ![12, 13, 14].includes(remainder100)) {
            return number + " года";
        }

        return number + " лет";
    },

    fromStr(str) {
        if (!str)
            str = "";

        str = str.trim();
        str = str.replace(" ", "T");
        str = str.replace(/\//g, "-");

        if (str.includes("+"))
            str = str.substr(0, str.indexOf("+"));

        if (!str.includes("T")) {
            if (!str.includes("-"))
                str = "1970-01-01T" + str;
            if (!str.includes(":"))
                str += "T00:00:00";

            str = str.replace("TT", "T");
        }

        return new Date(str);
    },

    getTimeToday(d) {
        const date = this.create(d);

        const dateCurr = new Date();
        dateCurr.setHours(date.getHours(), date.getMinutes(), date.getSeconds(), 0);

        return dateCurr;
    },

    toLocalTime(timeUTC) {
        const dateWithTimeUTC = new Date(`1970-01-01T${timeUTC}Z`);
        const localTime = dateWithTimeUTC.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });
        return localTime;
    },

    getAge(d) {
        const diff = Date.now() - this.create(d).getTime();
        const ageDt = new Date(diff);
        return Math.abs(ageDt.getUTCFullYear() - 1970);
    },

    create(d, useTz) {
        let date;

        if (!d)
            date = new Date();
        else if (d?.toFixed)
            date = new Date(d);
        else if (d?.getFullYear)
            date = new Date(d.getTime());
        else
            date = this.fromStr(d);

        if (useTz && this.tz)
            return this.addHours(date, this.tz);

        return date;
    },

    toUtc(d, returnStr = false) {
        const now = this.create(d);
        const date = new Date(now.getTime() + now.getTimezoneOffset() * 60000);

        return returnStr ? this.toStr(date) : date;
    },

    toTimeUTCString(d, short) {
        const timeInUTC = moment(d).utc().format(short ? 'HH:mm' : 'HH:mm:ss');
        return timeInUTC;
    },

    toLocal(d) {
        const date = moment.utc(d).local();
        return date.format('YYYY-MM-DDTHH:mm:ss.000+00:00');
    },

    toPeriodStr(d, d2) {
        const date = this.toDateStr(d);
        const start = this.toTimeStr(d);
        const end = this.toTimeStr(d2);

        return `${date}, ${start}-${end}`;
    },

    dateInRange(date, intervalStart, intervalEnd) {
        const targetDate = moment(date);
        const startDate = moment(intervalStart);
        const endDate = moment(intervalEnd);

        return targetDate.isSameOrAfter(startDate) && targetDate.isSameOrBefore(endDate);
    },

    intervalsOverlap(bd1, ed1, bd2, ed2) { // пресекаюются ли интервалы
        const beginDate = moment(bd1);
        const endDate = moment(ed1);
        const beginDate2 = moment(bd2);
        const endDate2 = moment(ed2);

        return beginDate.isBefore(endDate2) && beginDate2.isBefore(endDate);
    },

    intervalsOverlapTime(bd1, ed1, bd2, ed2) {
        const beginTime1Moment = moment(moment(bd1).format("HH:mm:ss"), "HH:mm:ss");
        const endTime1Moment = moment(moment(ed1).format("HH:mm:ss"), "HH:mm:ss");
        const beginTime2Moment = moment(moment(bd2).format("HH:mm:ss"), "HH:mm:ss");
        const endTime2Moment = moment(moment(ed2).format("HH:mm:ss"), "HH:mm:ss");

        return beginTime1Moment.isBefore(endTime2Moment) && beginTime2Moment.isBefore(endTime1Moment);
    },

    intervalExceedsBounds(bd1, ed1, bd2, ed2) { // входит лт интервал 2 в 1
        const beginDate1 = moment(bd1);
        const endDate1 = moment(ed1);
        const beginDate2 = moment(bd2);
        const endDate2 = moment(ed2);

        const startsBeforeFirst = beginDate2.isBefore(beginDate1);
        const endsAfterFirst = endDate2.isAfter(endDate1);

        return startsBeforeFirst || endsAfterFirst;
    },

    setTime(d, time) {
        const [hours, minutes] = time.split(':');
        return d.setHours(hours, minutes);
    },

    getNumberDayOfWeek(d) {
        const number = d.getDay() - 1;
        return number === -1 ? 6 : number;
    },

    isDateNotOlderThan(dateString, months, d) {
        const targetDate = moment(dateString);
        const currentDate = d ? moment(d) : moment();
        const cutoffDate = currentDate.subtract(months, 'months');
        return targetDate.isSameOrAfter(cutoffDate);
    },

    getFreeTimeForDoctor(doctorSh, visits) {
        const { beginDate, endDate } = doctorSh;
        const workStart = moment(beginDate);
        const workEnd = moment(endDate);

        const v = visits.filter(it => {
            const res = this.equealDates(new Date(this.toUtc(it.beginDate)), new Date(beginDate));
            return res;
        }).sort((a, b) => moment(a.beginDate).diff(moment(b.beginDate)));

        let previousEnd = workStart;
        let result = false;

        /*if (moment(beginDate) < moment("2025-02-19T23:59:59") && moment(beginDate) > moment("2025-02-19T00:00:00")) {
            console.log("v", v);
            console.log("doctorSh", doctorSh);
        }*/

        if (v.length) {
            for (const visit of v) {
                const visitStart = moment(visit.beginDate);
                const visitEnd = moment(visit.endDate);

                /*if (moment(visit.beginDate) < moment("2025-02-19T23:59:59") && moment(visit.beginDate) > moment("2025-02-19T00:00:00")) {
                    console.log("visitStart", visitStart);
                    console.log(`previousEnd`, previousEnd);
                }*/

                if (visitStart.diff(previousEnd, 'minutes') > 15) {
                    /*if (moment(visit.beginDate) < moment("2025-02-19T23:59:59") && moment(visit.beginDate) > moment("2025-02-19T00:00:00")) {
                        console.log("beginDate", moment(visit.beginDate));
                        console.log(`Свободное время найдено: с ${previousEnd.format('HH:mm')} до ${visitStart.format('HH:mm')}`);
                    }*/
                    result = true;
                    break;
                }

                previousEnd = visitEnd;
            }
        } else {
            result = true;
        }

        if (workEnd.diff(previousEnd, 'minutes') > 15)
            return true;

        //return false;
        return result;
    }
};

export default obj;
